import React from 'react';
import classNames from 'classnames';

import { ButtonSize, ButtonType } from 'ui/Button';
import IconButton from 'ui/IconButton';
import CalendarIcon from 'ui/icons/Calendar';
import useKeyDownCallback from 'toolkit/useKeyDownCallback';
import { KeyboardEventCode } from 'toolkit/types';

import styles from './index.module.scss';

interface CustomInputProps {
  elementId?: string;
  value?: string;
  onClick?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onMouseOver?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onMouseOut?: (event: React.MouseEvent<HTMLInputElement>) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  autoComplete?: string;
  label?: string;
}

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>((props, ref) => {
  const {
    value,
    onClick,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onMouseOver,
    onMouseOut,
    id,
    elementId,
    placeholder,
    disabled,
    required,
    label,
    ...rest
  } = props;

  const onKeyDownHandler = useKeyDownCallback<React.KeyboardEvent<HTMLInputElement>>((event) => onKeyDown?.(event), {
    include: [KeyboardEventCode.ENTER],
  });

  const inputId = id || elementId || 'date_picker_wrapper_default_id';

  return (
    <div className={classNames(styles.inputContainer)}>
      {label && (
        <label
          htmlFor={inputId}
          className={classNames(styles.label)}>
          {required && <span data-required>*</span>}
          {label}
        </label>
      )}

      <div
        className={styles.inputWrapperContainer}
        data-size="medium">
        <input
          type="text"
          data-size="medium"
          data-adornment-left="true"
          value={value || ''}
          id={inputId}
          data-testid={inputId}
          ref={ref}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={rest.autoComplete || 'on'}
          className={classNames({
            [styles.inputError]: false,
          })}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDownHandler}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...rest}
        />

        <div className={classNames(styles.inputWrapperContainerAdornment, styles.inputWrapperContainerAdornmentL)}>
          <IconButton
            onClick={onClick}
            size={ButtonSize.SMALL}
            type={ButtonType.PLAIN}
            elementId={`${inputId}_date_picker_wrapper_calendar_button`}
            className={styles.iconButton}>
            <CalendarIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
});

export default CustomInput;
